import g from '#core/global';
import events from '#core/events';
import load from '#core/load';
import layout from '#core/layout';
import util from '#core/util';

import VimeoPlayer from '@vimeo/player';


const state = window.teal_state = window.teal_state || {};
state.video = state.video || {};

const media_container_$ = '.media-figure';
const video_container_$ = '.media-figure__media';
const overlink_$ = '.video__overlink';
const controls_$ = '.video__control';
const html_player_$ = '.video__source';
const youtube_overlink_$ = '.video--youtube__overlink';
const youtube_static_$ = '.video--youtube__iframe.video--static__iframe';
const youtube_autoplay_$ = '.video--youtube__iframe.video--autoplay__iframe';
const vimeo_overlink_$ = '.video--vimeo__overlink';
const vimeo_static_$ = '.video--vimeo__iframe.video--static__iframe';
const vimeo_autoplay_$ = '.video--vimeo__iframe.video--autoplay__iframe';
const sproutvideo_overlink_$ = '.video--sproutvideo__overlink';
const sproutvideo_static_$ = '.video--sproutvideo__iframe.video--static__iframe';
const sproutvideo_autoplay_$ = '.video--sproutvideo__iframe.video--autoplay__iframe';
const sproutvideo_player_class = 'sproutvideo-player';
const sproutvideo_api_url = 'https://c.sproutvideo.com/player_api.js';
const transport_button_$ = '.button--transport--video';
const video_autoplay_url_attr = 'data-url';
const image_$ = '.video__image';
const poster_visible_attr = 'data-poster-visible';
const play_status_attr = 'data-playing';
const iframe_$ = 'iframe';
const iframe_attr = 'data-iframe-id';
const autoplay_deferred_attr = 'data-autoplay-deferred';

// Overshoot the exact container size to prevent rounding errors from causing
// the video to have visible borders at the edges
const size_overshoot = 1.02;
const aspect_ratio = 1.77777;

let has_video = false;
let $html_players = [];
let $youtube_overlinks = [];
let $youtube_statics = [];
let $youtube_autoplays = [];
let $vimeo_overlinks = [];
let $vimeo_statics = [];
let $vimeo_autoplays = [];
let $sproutvideo_overlinks = [];
let $sproutvideo_statics = [];
let $sproutvideo_autoplays = [];
let $transport_buttons = [];
let sproutvideo_api_promise = null;
let previous_width = 0;
let previous_height = 0;


const module = {
	
	/**
	 * Init module
	 */
	init() {
		if (load.ran('video')) return false;
		
		state.video.init_youtube_players = () => {
			this.initYouTube();
		};
		
		load.content(() => {
			has_video = assign_elements();
			if (!has_video) return false;
			
			window.addEventListener(g.resize_e, util.throttle(() => {
				update_ambient_video_size();
			}, 150));
			update_ambient_video_size();
			
			if (state.video.init_youtube_players_ready) {
				state.video.init_youtube_players();
			}
		});
		load.all(() => {
			has_video = assign_elements();
			if (!has_video) return false;
			
			update_ambient_video_size();
			assign_change_events();
		});
	},
	
	
	/**
	 * Init YouTube iframe player API
	 * @link https://developers.google.com/youtube/iframe_api_reference
	 */
	initYouTube() {
		$youtube_autoplays.forEach($player => {
			setup_youtube_player($player);
		});
		update_ambient_video_size();
	},
	
};

export default module;
module.init();


/**
 * Assign elements and event listeners
 * @return {boolean}
 */
function assign_elements() {
	if (has_video) return true;
	
	// TODO: do not store objects on load, account for dynamic content
	
	// TODO: make entire frame clickable like overlink
	$html_players = document.querySelectorAll(html_player_$);
	
	$youtube_overlinks = document.querySelectorAll(youtube_overlink_$);
	$youtube_statics = document.querySelectorAll(youtube_static_$);
	$youtube_autoplays = document.querySelectorAll(youtube_autoplay_$);
	
	$vimeo_overlinks = document.querySelectorAll(vimeo_overlink_$);
	$vimeo_statics = document.querySelectorAll(vimeo_static_$);
	$vimeo_autoplays = document.querySelectorAll(vimeo_autoplay_$);

	$sproutvideo_overlinks = document.querySelectorAll(sproutvideo_overlink_$);
	$sproutvideo_statics = document.querySelectorAll(sproutvideo_static_$);
	$sproutvideo_autoplays = document.querySelectorAll(sproutvideo_autoplay_$);
	
	$transport_buttons = document.querySelectorAll(transport_button_$);
	$transport_buttons.forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			toggle_video_playback($toggle);
		});
	});
	
	$html_players.forEach($player => {
		
		// Disable autoplay for users who prefer reduced motion or have data saver enabled
		const reduced_motion = window.matchMedia('(prefers-reduced-motion: reduce)');
		const is_reduced = (
			reduced_motion === true
			|| reduced_motion.matches === true
		);
		const save_data = navigator.connection?.saveData === true;
		const should_disable_autoplay = is_reduced || save_data;
		
		if (should_disable_autoplay) {
			$player.removeAttribute(autoplay_deferred_attr);
		}
		
		// Handle deferred autoplay: wait for idle and visibility before playing
		if (
			$player.hasAttribute(autoplay_deferred_attr)
			&& !should_disable_autoplay
		) {
			init_deferred_autoplay($player);
		}
		
		[g.play_e, g.pause_e].forEach(event_name => {
			$player.addEventListener(event_name, e => {
				update_play_status($player, event_name === g.play_e);
			});
		});
	});
	
	$youtube_overlinks.forEach($overlink => {
		$overlink.addEventListener(g.click_e, e => {
			load_youtube_by_click(e);
		});
	});
	
	$youtube_autoplays.forEach($autoplay => {
		const url = $autoplay.getAttribute(video_autoplay_url_attr);
		load_iframe_video(url, $autoplay);
	});
	
	$vimeo_overlinks.forEach($overlink => {
		$overlink.addEventListener(g.click_e, e => {
			load_vimeo_by_click(e);
		});
	});
	
	$vimeo_autoplays.forEach($autoplay => {
		const url = $autoplay.getAttribute(video_autoplay_url_attr);
		load_iframe_video(url, $autoplay, () => {
			setup_vimeo_player($autoplay);
		});
	});

	$sproutvideo_overlinks.forEach($overlink => {
		$overlink.addEventListener(g.click_e, e => {
			load_sproutvideo_by_click(e);
		});
	});

	$sproutvideo_autoplays.forEach($autoplay => {
		const url = $autoplay.getAttribute(video_autoplay_url_attr);
		load_iframe_video(url, $autoplay, () => {
			setup_sproutvideo_player($autoplay);
		});
	});

	return true;
}


/**
 * Assign change events
 */
function assign_change_events() {
	document.addEventListener(events.layout.change, e => {
		update_ambient_video_size();
	});
}


/**
 * Initialize deferred autoplay for a video element
 * Wait for page to be idle and video to be in viewport before playing
 * @param {element} $video
 */
function init_deferred_autoplay($video) {
	let has_played = false;
	
	const play_video = () => {
		if (has_played) return;
		has_played = true;
		$video.play().catch(() => {
			// Autoplay was prevented, user interaction required
		});
	};
	
	const schedule_play = () => {
		if ('requestIdleCallback' in window) {
			requestIdleCallback(play_video, {
				timeout: 2000,
			});
		} else {
			setTimeout(play_video, 100);
		}
	};
	
	// Use IntersectionObserver to wait for visibility
	const observer = new IntersectionObserver((entries) => {
		entries.forEach(entry => {
			if (entry.isIntersecting) {
				observer.disconnect();
				schedule_play();
			}
		});
	}, {
		threshold: 0.1,
	});
	
	observer.observe($video);
	
	// Fallback: if video is above the fold, observer may have timing issues
	// Schedule play after a short delay as backup
	setTimeout(() => {
		if (!has_played) {
			const rect = $video.getBoundingClientRect();
			const is_visible = (
				rect.top < window.innerHeight
				&& rect.bottom > 0
			);
			if (is_visible) {
				observer.disconnect();
				schedule_play();
			}
		}
	}, 500);
}


/**
 * Toggle video playback for HTML videos
 * @param {element} $toggle
 */
function toggle_video_playback($toggle) {
	const video_id = $toggle.getAttribute('aria-controls');
	if (!video_id) return false;
	
	const $iframe_video = document.querySelector(`[${iframe_attr}="${video_id}"]`);
	if ($iframe_video) {
		const $overlink = $iframe_video.querySelector(overlink_$);
		if ($overlink) {
			$overlink.click();
			return false;
		}
	}
	
	const $video = document.querySelector(`#${video_id}`);
	if (!$video) return false;
	
	if (is_currently_playing($video)) {
		$video.pause();
	} else {
		$video.play();
	}
}


/**
 * Update video play status
 * @param {element} $video
 * @param {boolean} is_playing
 */
function update_play_status($video, is_playing) {
	const $toggle = document.querySelector(`[aria-controls="${$video.id}"]`);
	if (!$toggle) return false;
	
	const $container = $video.closest(media_container_$);
	
	if (is_playing) {
		$container.setAttribute(play_status_attr, g.true_s);
		
		// Remove image so it doesn't peek through at certain viewport sizes, but
		// only if the poster is not shown on pause (wait for "canplay" to ensure
		// the first video frame is ready before hiding the poster)
		const $image = $video
			.closest(media_container_$)
			.querySelector(`${image_$}:not([${poster_visible_attr}])`)
		;
		if ($image) {
			if ($video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
				// Video already has enough data
				$image.remove();
			} else {
				// Wait for video to have enough data before removing poster
				$video.addEventListener('canplay', () => {
					$image.remove();
				}, { once: true });
			}
		}
	} else {
		$container.setAttribute(play_status_attr, g.false_s);
	}
}


/**
 * Load YouTube video by click
 * @param {event} e
 */
function load_youtube_by_click(e) {
	e.preventDefault();
	e.stopPropagation();
	
	const $container = e.target.closest(video_container_$);
	const $link = e.target.closest(overlink_$);
	load_iframe_video($link.getAttribute('href'), $container, () => {
		$container.querySelector(controls_$).remove();
		$container.querySelector(overlink_$).remove();
		setup_youtube_player($container, () => {
			$container.querySelectorAll(':scope > :not(iframe)').forEach($el => {
				$el.remove();
			});
		});
	});
}


/**
 * Load Vimeo video by click
 * @param {event} e
 */
function load_vimeo_by_click(e) {
	e.preventDefault();
	e.stopPropagation();
	
	const $container = e.target.closest(video_container_$);
	const $link = e.target.closest(overlink_$);
	load_iframe_video($link.getAttribute('href'), $container, () => {
		$container.querySelector(controls_$).remove();
		$container.querySelector(overlink_$).remove();
		setup_vimeo_player($container, () => {
			$container.querySelectorAll(':scope > :not(iframe)').forEach($el => {
				$el.remove();
			});
		});
	});
}


/**
 * Load Sprout Video video by click
 * @param {event} e
 */
function load_sproutvideo_by_click(e) {
	e.preventDefault();
	e.stopPropagation();

	const $container = e.target.closest(video_container_$);
	const $link = e.target.closest(overlink_$);
	load_iframe_video($link.getAttribute('href'), $container, () => {
		$container.querySelector(controls_$).remove();
		$container.querySelector(overlink_$).remove();
		setup_sproutvideo_player($container, () => {
			$container.querySelectorAll(':scope > :not(iframe)').forEach($el => {
				$el.remove();
			});
		});
	});
}


/**
 * Load video in iframe
 * @param {string} url
 * @param {element} $container
 * @param {function} load_callback
 */
function load_iframe_video(url, $container, load_callback) {
	const $iframe = create_iframe(url, $container);
	$container.prepend($iframe);
	
	if (load_callback) load_callback();
}


/**
 * Create iframe
 * @param {string} url
 * @param {element} $container
 * @return {element}
 */
function create_iframe(url, $container) {
	const $iframe = document.createElement(iframe_$);
	const iframe_id = $container.getAttribute(iframe_attr);
	$iframe.src = clean_url(url);
	$iframe.setAttribute('id', `iframe-${iframe_id}`);
	$iframe.setAttribute('frameborder', 0);
	$iframe.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; fullscreen; picture-in-picture');
	$iframe.setAttribute('allowfullscreen', true);
	return $iframe;
}


/**
 * Clean URL
 * @param {string} url
 * @return {string}
 */
function clean_url(url) {
	if (!url.includes('vimeo')) return url;
	
	// return clean_vimeo_url(url);
	return url;
}


/**
 * Make Vimeo URL compatible with embed
 * @param {string} url
 * @return {string}
 */
function clean_vimeo_url(url) {
	const id = get_vimeo_id(url);
	if (!id) return url;
	
	return `https://player.vimeo.com/video/${id}?autoplay=1`;
}


/**
 * Extract Vimeo video ID from URL
 * @param {string} url
 * @return {string}
 */
function get_vimeo_id(url) {
	const regex = /https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
	const parts = url.match(regex);
	return (parts)
		? parts[1]
		: null;
}


/**
 * Extract YouTube video ID from URL
 * @param {string} url
 * @return {string}
 */
function get_youtube_id(url) {
	const regex = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/gi;
	const parts = url.match(regex);
	return (parts)
		? parts[1]
		: null;
}


/**
 * Update ambient video size to fit any aspect ratio
 */
function update_ambient_video_size() {
	$youtube_autoplays.forEach($autoplay => {
		// Scale the video much wider than its container, in order to crop out the
		// video title and info that otherwise can't be hidden
		const youtube_width_scale = 5;
		update_video_size($autoplay, youtube_width_scale);
	});
	$vimeo_autoplays.forEach($autoplay => {
		update_video_size($autoplay);
	});
	$sproutvideo_autoplays.forEach($autoplay => {
		update_video_size($autoplay);
	});
}


/**
 * Update video size to fill container without letterboxing or pillarboxing
 * @param {element} $container
 * @param {float} width_scale
 */
function update_video_size($container, width_scale = 1) {
	const width = layout.getWidth($container);
	const height = layout.getHeight($container);
	if (!width || !height) return false;
	if (width === previous_width && height === previous_height) return false;
	
	previous_width = width;
	previous_height = height;
	
	// TODO: cache iframes
	const $iframe = $container.querySelector(iframe_$);
	
	// TODO: see video.flexible_ratio property
	// if ($container.classList.contains(':flex-ratio-true')) {
	// }
	
	if (width / height > aspect_ratio) {
		$iframe.style.width = `${100 * width_scale * size_overshoot}%`;
		// TODO: this height calculation is off, too large
		$iframe.style.height = `${Math.ceil((width * size_overshoot) * (1 / aspect_ratio))}px`;
	} else {
		// TODO: this width calculation needs to factor in the width scale
		$iframe.style.width = `${Math.ceil((height * size_overshoot) * aspect_ratio)}px`;
		$iframe.style.height = `${100 * size_overshoot}%`;
	}
}


/**
 * Is this video currently playing?
 * @param {element} $video
 * @return {boolean}
 */
function is_currently_playing($video) {
	return (
		!$video.paused
		&& !$video.ended
		&& $video.readyState > HTMLMediaElement.HAVE_CURRENT_DATA
		// && $video.currentTime > 0 // TODO: this is problematic
	);
}


/**
 * Setup YouTube player
 * @param {element} $player
 * @param {function} play_callback
 */
function setup_youtube_player($player, play_callback) {
	const $iframe = $player.querySelector(iframe_$);
	if (!$iframe) return false;
	
	let ran_play_callback = false;
	const player = new YT.Player($iframe.getAttribute('id'), {
		playerVars: {
			origin: window.location.origin,
		},
		events: {
			onReady: on_ready,
			onStateChange: on_state_change,
		}
	});
	
	// Toggle playback by remote button
	const $container = $player.closest(media_container_$);
	$container.querySelectorAll(transport_button_$).forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			const play_status = player.getPlayerState();
			if (play_status !== YT.PlayerState.PAUSED) {
				player.pauseVideo();
			} else {
				player.playVideo();
			}
		});
	});
	
	
	/**
	 * Handle player ready event
	 * @param {event} e
	 */
	function on_ready(e) {
		const play_status = player.getPlayerState();
		const $container = $player.closest(media_container_$);
		
		// Check if autoplay video is already playing and update controls
		if (play_status === YT.PlayerState.PLAYING) {
			update_controls($container, true);
			if (play_callback && !ran_play_callback) {
				play_callback();
				ran_play_callback = true;
			}
		}
		
		// Add fallback polling to catch any missed state changes
		let poll_count = 0;
		const max_polls = 10;
		const poll_interval = 300;
		const state_poll = setInterval(() => {
			poll_count++;
			const current_state = player.getPlayerState();
			
			if (
				current_state === YT.PlayerState.PLAYING
				&& $container.getAttribute(play_status_attr) !== g.true_s
			) {
				update_controls($container, true);
				if (play_callback && !ran_play_callback) {
					play_callback();
					ran_play_callback = true;
				}
			}
			
			if (
				poll_count >= max_polls
				|| $container.getAttribute(play_status_attr) === g.true_s
			) {
				clearInterval(state_poll);
			}
		}, poll_interval);
	}
	
	
	/**
	 * Receive status on player state change
	 * @param {event} e
	 */
	function on_state_change(e) {
		const play_status = e.data;
		update_controls($container, play_status !== YT.PlayerState.PAUSED);
		
		if (play_status === YT.PlayerState.PLAYING) {
			if (play_callback && !ran_play_callback) {
				play_callback();
				ran_play_callback = true;
			}
		}
	}
	
}


/**
 * Setup Vimeo player
 * @param {element} $player
 * @param {function} play_callback
 */
function setup_vimeo_player($player, play_callback) {
	const $iframe = $player.querySelector(iframe_$);
	if (!$iframe) return false;
	
	let ran_play_callback = false;
	const player = new VimeoPlayer($iframe);
	
	// Toggle playback by remote button
	const $container = $player.closest(media_container_$);
	$container.querySelectorAll(transport_button_$).forEach($toggle => {
		$toggle.addEventListener(g.click_e, e => {
			player.getPaused().then(paused => {
				if (!paused) {
					player.pause();
				} else {
					player.play();
				}
			}).catch(error => {
			});
		});
	});
	
	player.on('play', e => {
		update_controls($container, true);
		if (play_callback && !ran_play_callback) {
			play_callback();
			ran_play_callback = true;
		}
	});
	player.on('pause', e => {
		update_controls($container, false);
	});
	
}


/**
 * Setup Sprout Video player
 * @link https://sproutvideo.com/help/articles/27-javascript_player_api
 * @param {element} $player
 * @param {function} play_callback
 */
function setup_sproutvideo_player($player, play_callback) {
	const $iframe = $player.querySelector(iframe_$);
	if (!$iframe) return false;

	$iframe.classList.add(sproutvideo_player_class);

	load_sproutvideo_api(() => {
		const video_id = get_sproutvideo_id($iframe.getAttribute('src'));
		if (!video_id) return false;

		let ran_play_callback = false;
		const player = new SV.Player({
			videoId: video_id,
		});

		// Toggle playback by remote button
		const $container = $player.closest(media_container_$);
		$container.querySelectorAll(transport_button_$).forEach($toggle => {
			$toggle.addEventListener(g.click_e, e => {
				if ($container.getAttribute(play_status_attr) === g.true_s) {
					player.pause();
				} else {
					player.play();
				}
			});
		});

		player.bind('play', e => {
			update_controls($container, true);
			if (play_callback && !ran_play_callback) {
				play_callback();
				ran_play_callback = true;
			}
		});
		player.bind('pause', e => {
			update_controls($container, false);
		});
	});
}


/**
 * Load the Sprout Video player API script once, then run the callback
 * @param {function} callback
 */
function load_sproutvideo_api(callback) {
	if (window.SV) {
		callback();
		return;
	}

	if (!sproutvideo_api_promise) {
		sproutvideo_api_promise = new Promise(resolve => {
			const $script = document.createElement('script');
			$script.src = sproutvideo_api_url;
			$script.addEventListener('load', resolve);
			document.head.appendChild($script);
		});
	}

	sproutvideo_api_promise.then(callback);
}


/**
 * Extract Sprout Video video ID from an embed URL
 * @param {string} url
 * @return {string}
 */
function get_sproutvideo_id(url) {
	const parts = url.match(/\/embed\/([a-z0-9]+)/i);
	return (parts)
		? parts[1]
		: null;
}


/**
 * Update player controls based on player state
 * @param {element} $container
 * @param {boolean} is_playing
 */
function update_controls($container, is_playing) {
	$container.setAttribute(play_status_attr, is_playing
		? g.true_s
		: g.false_s
	);
}
